export type AppRole = 'seeker' | 'provider'

export type SeekerView = 'cv' | 'find-companies'
export type ProviderView = 'publish' | 'search-employee'

export const SEEKER_ROUTES: Record<SeekerView, string> = {
  cv: '/app/cv',
  'find-companies': '/app/find-companies',
}

export const PROVIDER_ROUTES: Record<ProviderView, string> = {
  publish: '/app/publish',
  'search-employee': '/app/search-employee',
}

/** Role-agnostic inbox routes shared by both seeker and provider navigation. */
export const INBOX_ROUTES = {
  root: '/app/inbox',
  business: '/app/inbox/business',
  personal: '/app/inbox/personal',
  hiring: '/app/inbox/hiring',
} as const

/** Default inbox entry (redirects to business or personal). */
export const INBOX_ROUTE = INBOX_ROUTES.root

/** Home feed (content built later). */
export const HOME_ROUTE = '/app/home'

/** Shein-style shopping mall inside the app shell. */
export const MALL_ROUTE = '/app/mall'

/** Shop directory inside Vntru shopping. */
export const SHOPS_ROUTE = '/app/shops'

/** Combined "Profile" hub: the seeker CV form and the company profile. */
export const PROFILE_ROUTE = '/app/profile'

/** Creator / developer workspace for online stores. */
export const CREATOR_ROUTE = '/app/creator'

/** Platform support chat. Opens WhatsApp to this number. */
export const VNTRU_SUPPORT_WHATSAPP_HREF = 'https://wa.me/201550087506'

/** Empty messaging tab inside the creator workspace. */
export const CREATOR_MESSAGES_ROUTE = '/app/creator/messages'

/** Super-admin only maintenance dashboard. */
export const ADMIN_ROUTE = '/app/admin'

/** Unified search screen with Employee / Jobs / Companies tabs. */
export const SEARCH_ROUTE = '/app/search'

export const APP_TAB_PATHS = [
  HOME_ROUTE,
  MALL_ROUTE,
  SHOPS_ROUTE,
  ...Object.values(SEEKER_ROUTES),
  ...Object.values(PROVIDER_ROUTES),
  ...Object.values(INBOX_ROUTES),
  PROFILE_ROUTE,
  CREATOR_ROUTE,
  ADMIN_ROUTE,
  SEARCH_ROUTE,
] as const

const SEEKER_VIEWS = Object.keys(SEEKER_ROUTES) as SeekerView[]
const PROVIDER_VIEWS = Object.keys(PROVIDER_ROUTES) as ProviderView[]

const PATH_TO_SEEKER_VIEW = Object.fromEntries(
  Object.entries(SEEKER_ROUTES).map(([view, path]) => [path, view]),
) as Record<string, SeekerView>

const PATH_TO_PROVIDER_VIEW = Object.fromEntries(
  Object.entries(PROVIDER_ROUTES).map(([view, path]) => [path, view]),
) as Record<string, ProviderView>

export function useAppNavigation() {
  const route = useRoute()
  const router = useRouter()
  const role = useState<AppRole>('app-role', () => 'seeker')

  const seekerView = computed<SeekerView>(() => {
    return PATH_TO_SEEKER_VIEW[route.path] ?? 'cv'
  })

  const providerView = computed<ProviderView>(() => {
    return PATH_TO_PROVIDER_VIEW[route.path] ?? 'publish'
  })

  const isInbox = computed(() => route.path.startsWith(INBOX_ROUTES.root))

  watch(
    () => route.path,
    (path) => {
      if (PATH_TO_SEEKER_VIEW[path]) {
        role.value = 'seeker'
      }
      else if (PATH_TO_PROVIDER_VIEW[path]) {
        role.value = 'provider'
      }
    },
    { immediate: true },
  )

  function switchRole(next: AppRole) {
    role.value = next
    if (next === 'seeker') return router.push(SEEKER_ROUTES.cv)
    const { account } = useProfileAccount()
    if (account.value === 'company') {
      const { requestMode } = useJobsHubMode()
      requestMode('hire')
      return router.push(SEEKER_ROUTES.cv)
    }
    return router.push(PROVIDER_ROUTES.publish)
  }

  function switchSeekerView(view: SeekerView) {
    const next = SEEKER_VIEWS.includes(view) ? view : 'cv'
    return router.push(SEEKER_ROUTES[next])
  }

  function switchProviderView(view: ProviderView) {
    const next = PROVIDER_VIEWS.includes(view) ? view : 'publish'
    return router.push(PROVIDER_ROUTES[next])
  }

  return {
    role,
    seekerView,
    providerView,
    isInbox,
    switchRole,
    switchSeekerView,
    switchProviderView,
    SEEKER_VIEWS,
    PROVIDER_VIEWS,
    SEEKER_ROUTES,
    PROVIDER_ROUTES,
    INBOX_ROUTE,
    INBOX_ROUTES,
    HOME_ROUTE,
    MALL_ROUTE,
    SHOPS_ROUTE,
    PROFILE_ROUTE,
    CREATOR_ROUTE,
    CREATOR_MESSAGES_ROUTE,
    ADMIN_ROUTE,
    SEARCH_ROUTE,
    APP_TAB_PATHS,
  }
}
